import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { formatNumber } from '@/utils/locale';

interface InlineMileageEditProps {
  value: number | null;
  onSave: (mileage: number) => void | Promise<void>;
  saving: boolean;
}

export function InlineMileageEdit({
  value,
  onSave,
  saving,
}: InlineMileageEditProps) {
  const { t } = useTranslation('workorders');
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    setEditValue(value != null ? String(value) : '');
    setEditing(true);
    setTimeout(() => { inputRef.current?.focus(); }, 50);
  };

  const handleSave = () => {
    const num = parseInt(editValue, 10);
    if (!isNaN(num) && num >= 0) {
      void onSave(num);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') handleSave();
    if (event.key === 'Escape') handleCancel();
  };

  const parsedValue = parseInt(editValue, 10);
  const isLowerThanCurrent = editing && !isNaN(parsedValue) && value != null && parsedValue < value;

  if (editing) {
    return (
      <Box>
        <TextField
          inputRef={inputRef}
          size="small"
          type="number"
          value={editValue}
          onChange={(event) => { setEditValue(event.target.value); }}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={saving}
          color={isLowerThanCurrent ? 'warning' : undefined}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">km</InputAdornment>,
            },
          }}
          sx={{
            width: 180,
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': { MozAppearance: 'textfield' },
          }}
        />
        {value != null && (
          <Typography variant="caption" color={isLowerThanCurrent ? 'warning.main' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
            {isLowerThanCurrent
              ? t('create.mileageLowerThanLatest', { latest: formatNumber(value) })
              : t('detail.mileageValue', { km: formatNumber(value) })}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
      onClick={startEditing}
    >
      <Typography variant="body2">
        {value != null
          ? t('detail.mileageValue', { km: formatNumber(value) })
          : '—'}
      </Typography>
      <EditIcon sx={{ fontSize: 14, color: 'action.active' }} />
    </Box>
  );
}
